import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  CloudUpload,
  Database,
  HardDrive,
  RefreshCw,
  RotateCcw,
  Plus,
} from "lucide-react";
import moment from "moment";
import { toast } from "sonner";

import AddButtonOverlay from "src/components/common/AddButtonOverlay";
import CgmPeriodNavigator from "../cgm/CgmPeriodNavigator";
import CgmReadingCard from "../cgm/CgmReadingCard";
import CgmMetricCard from "../cgm/CgmMetricCard";
import DayGlucoseTrendChart from "../graphs/cgm/daily/DayGlucoseTrendChart";
import PieChartRepresentation from "../graphs/cgm/cumulative/PieChartRepresentation";
import CGMHardSyncBottomSheet from "src/components/app/bottom-sheet/cgm/CGMHardSyncBottomSheet";
import CGMUploadLogsBottomSheet from "src/components/app/bottom-sheet/cgm/CGMUploadLogsBottomSheet";
import CGMClearRestartBottomSheet from "src/components/app/bottom-sheet/cgm/CGMClearRestartBottomSheet";
import CGMDetailedLogsBottomSheet from "../bottom-sheet/cgm/CGMDetailedLogsBottomSheet";

import { useAppSelector, useAppDispatch } from "src/redux/hooks";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import { CGM_IN_RANGE, CGM_READER_DEVICE_ID } from "src/constants/cgm";
import { parseCgmMessage } from "src/services/cgm";
import { cgmManager } from "src/services/cgm/cgmManager";
import type { CgmMetricItem, CgmStatusData } from "src/types/cgm.types";
import * as cgmActions from "src/redux/slices/cgmSlice";

interface ChartReading {
  time: string;
  value: number;
  currPosition?: string;
}

const tabs = [
  { id: "daily", title: "Daily" },
  { id: "cumulative", title: "Cumulative" },
];

function formatTime(ts: string): string {
  const d = moment(ts);
  return d.isValid() ? d.format("hh:mm A") : "";
}

const InsightComponentGlucose: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  const cgm = useAppSelector((state) => state.cgm);
  const { connectedDevices } = useAppSelector((state) => state.device);
  const { dailyLogs, cgmRemoteRefreshNonce, statusAndSensorLife } = cgm;

  const [activeTab, setActiveTab] = useState<string>("daily");

  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [showHardSyncSheet, setShowHardSyncSheet] = useState(false);
  const [showUploadLogsSheet, setShowUploadLogsSheet] = useState(false);
  const [showClearRestartSheet, setShowClearRestartSheet] = useState(false);
  const [activeDetailedLogsSheet, setActiveDetailedLogsSheet] = useState<
    string | null
  >(null);

  const isToday = moment(selectedDate).isSame(moment(), "day");

  const INCREMENTAL_SYNC_COOLDOWN_MS = 45_000;
  const DAILY_LOGS_REFETCH_ON_LIVE_COOLDOWN_MS = 30_000;
  /** Debounce duplicate visibility events; still allows refetch every time user returns after a few seconds away. */
  const VISIBILITY_DAILY_LOGS_DEBOUNCE_MS = 3_000;
  const DISCONNECT_RECONNECT_THROTTLE_MS = 60_000;
  const lastIncrementalSyncRef = useRef(0);
  const lastVisibilityDailyLogsRefetchRef = useRef(0);
  const lastDailyLogsLiveRefetchRef = useRef(0);
  const lastDisconnectRefetchRef = useRef(0);
  const prevCgmApiStatusRef = useRef<string | undefined>(undefined);
  const cgmRef = useRef(cgm);
  cgmRef.current = cgm;

  // activeCgm
  const activeCgm = statusAndSensorLife?.data?.[0];

  const isCgmExpired = useMemo(() => {
    return activeCgm?.status === "EXPIRED";
  }, [activeCgm]);

  /** Full transmitter unpair (TRANSMITTER_DISCONNECT); soft link loss is DISCONNECTED. */
  const isCgmDisconnected = useMemo(() => {
    return activeCgm?.status === "TRANSMITTER_DISCONNECT";
  }, [activeCgm]);

  /**
   * Keep RN in the loop when the backend says soft disconnect or full transmitter disconnect:
   * refetch status (throttled). Previously grouped with EXPIRED, which skipped refetch.
   */
  useEffect(() => {
    if (!activeCgm) {
      prevCgmApiStatusRef.current = undefined;
      return;
    }

    const isPastEnd = activeCgm.endDate < new Date().toISOString();
    const isExpired = activeCgm.status === "EXPIRED" || isPastEnd;
    if (isExpired) {
      prevCgmApiStatusRef.current = activeCgm.status;
      return;
    }

    if (
      activeCgm.status === "DISCONNECTED" ||
      activeCgm.status === "TRANSMITTER_DISCONNECT"
    ) {
      const now = Date.now();
      const prev = prevCgmApiStatusRef.current;
      const isFirstDisconnect =
        prev !== "DISCONNECTED" && prev !== "TRANSMITTER_DISCONNECT";
      if (
        !isFirstDisconnect &&
        now - lastDisconnectRefetchRef.current <
          DISCONNECT_RECONNECT_THROTTLE_MS
      ) {
        prevCgmApiStatusRef.current = activeCgm.status;
        return;
      }
      lastDisconnectRefetchRef.current = now;
      cgmManager.refetchCgmStatus(undefined, cgmRef.current);
      prevCgmApiStatusRef.current = activeCgm.status;
      return;
    }

    cgmManager.refetchCgmStatus(undefined, cgmRef.current);
    prevCgmApiStatusRef.current = activeCgm.status;
  }, [activeCgm]);

  // Mount: pull latest from device + POST (CGM_GET_LATEST_READING)
  // useEffect(() => {
  //   cgmManager.getLatestReading();
  // }, []);

  // Toasts when RN completes sync operations (daily logs refetched in handleCgmMessage on success)
  const prevHardRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevHardRef.current;
    prevHardRef.current = cgm.cgmHardSync.isLoading;
    if (wasLoading && !cgm.cgmHardSync.isLoading) {
      if (cgm.cgmHardSync.error) toast.error("Sync Error!");
      else {
        toast.success("Device Synced");
        dispatch(cgmActions.getDailyLogsRequest({ startDate: selectedDate }));
      }
    }
  }, [
    cgm.cgmHardSync.isLoading,
    cgm.cgmHardSync.error,
    selectedDate,
    dispatch,
  ]);

  // Latest reading sync
  const prevGetLatestRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevGetLatestRef.current;
    prevGetLatestRef.current = cgm.cgmGetLatest.isLoading;
    if (wasLoading && !cgm.cgmGetLatest.isLoading) {
      if (cgm.cgmGetLatest.error) toast.error("Could not sync latest reading");
      else toast.success("Sync completed"); // toast.success("Updated from device");
    }
  }, [cgm.cgmGetLatest.isLoading, cgm.cgmGetLatest.error]);

  // Missing points sync
  const prevMissingRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevMissingRef.current;
    prevMissingRef.current = cgm.cgmMissingSync.isLoading;
    if (wasLoading && !cgm.cgmMissingSync.isLoading) {
      if (cgm.cgmMissingSync.error) toast.error("Sync failed");
      // toast.error("Missing points sync failed");
      else toast.success("Sync completed"); // toast.success("Missing points synced");

      dispatch(cgmActions.getDailyLogsRequest({ startDate: selectedDate }));
    }
  }, [
    cgm.cgmMissingSync.isLoading,
    cgm.cgmMissingSync.error,
    selectedDate,
    dispatch,
  ]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = parseCgmMessage(event);
      if (!payload || payload.action !== POST_MESSAGE_EVENTS.CGM_LOGS_UPLOADED)
        return;
      const data = payload.data as Record<string, unknown> | undefined;
      if (data?.success) {
        toast.success("Upload successful");
      } else {
        const detail = data?.error as string | undefined;
        toast.error("Upload failed!", {
          description: detail || undefined,
        });
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      dispatch(cgmActions.getDailyLogsRequest({ startDate: selectedDate }));
    }
  }, [selectedDate, dispatch]);

  // RN incremental sync or live API upload → refetch daily logs for the date in view
  useEffect(() => {
    if (cgmRemoteRefreshNonce === 0) return;
    dispatch(cgmActions.getDailyLogsRequest({ startDate: selectedDate }));
  }, [cgmRemoteRefreshNonce, selectedDate, dispatch]);

  const latestLiveReadingMs = useMemo(() => {
    const readings =
      cgm.latestReadings?.length > 0
        ? cgm.latestReadings
        : cgm.cgmConnectionLive?.readings ?? [];
    let best = 0;
    for (const r of readings) {
      const t = r.timeInMillis;
      if (typeof t === "number" && Number.isFinite(t) && t > best) best = t;
    }
    return best;
  }, [cgm.latestReadings, cgm.cgmConnectionLive?.readings]);

  // Live RN glucose → refresh daily_logs so the chart matches CgmReadingCard (throttled).
  useEffect(() => {
    if (!isToday) return;
    const hasCgmReader = connectedDevices?.data?.some(
      (d) => d.id === CGM_READER_DEVICE_ID
    );
    if (!hasCgmReader) return;
    if (latestLiveReadingMs === 0) return;

    const now = Date.now();
    if (
      now - lastDailyLogsLiveRefetchRef.current <
      DAILY_LOGS_REFETCH_ON_LIVE_COOLDOWN_MS
    ) {
      return;
    }
    lastDailyLogsLiveRefetchRef.current = now;
    dispatch(cgmActions.getDailyLogsRequest({ startDate: selectedDate }));
  }, [
    latestLiveReadingMs,
    isToday,
    selectedDate,
    dispatch,
    connectedDevices?.data,
  ]);

  // WebView visible again: refresh status, refetch daily_logs for the graph, throttled latest reading
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;

      const hasCgmReader = connectedDevices?.data?.some(
        (d) => d.id === CGM_READER_DEVICE_ID
      );
      if (!hasCgmReader) return;

      cgmManager.refetchCgmStatus(selectedDate, cgmRef.current);

      const now = Date.now();
      // Chart uses daily_logs API only — refetch on foreground so graph catches up after sync.
      if (
        now - lastVisibilityDailyLogsRefetchRef.current >=
        VISIBILITY_DAILY_LOGS_DEBOUNCE_MS
      ) {
        lastVisibilityDailyLogsRefetchRef.current = now;
        dispatch(cgmActions.getDailyLogsRequest({ startDate: selectedDate }));
      }

      if (now - lastIncrementalSyncRef.current < INCREMENTAL_SYNC_COOLDOWN_MS)
        return;
      lastIncrementalSyncRef.current = now;
      cgmManager.getLatestReading();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [selectedDate, dispatch, connectedDevices?.data]);

  const chartData = useMemo(() => {
    const trend = dailyLogs.data?.dailyGlucoseTrend ?? [];
    return trend.map((item: Record<string, unknown>) => ({
      time: formatTime((item.readingTimestamp as string) ?? ""),
      value: (item.glucoseValue as number) ?? 0,
      currPosition: item.currPosition as string | undefined,
    })) as ChartReading[];
  }, [dailyLogs.data]);

  const basicMetrics: CgmMetricItem[] = dailyLogs.data?.basicMetrics ?? [];
  const advancedMetrics: CgmMetricItem[] =
    dailyLogs.data?.advancedMetrics ?? [];
  const metrics: CgmMetricItem[] = [...basicMetrics, ...advancedMetrics];

  // activeCgm?.status === "WARMUP" ||
  // (activeCgm?.boundDevice != null && activeCgm?.step !== "intro");

  // const isCgmDisconnected = useMemo(() => {
  //   return activeCgm?.status === "DISCONNECTED";
  // }, [activeCgm]);

  const activeCgmOverlayOptions = useMemo(
    () => [
      {
        icon: <RefreshCw className="w-5 h-5" />,
        label: "Sync",
        // onClick: () => cgmManager.getLatestReading(),
        onClick: () => cgmManager.syncMissingPoints(),
      },
      // {
      //   icon: <Database className="w-5 h-5" />,
      //   label: "Sync missing points",
      //   onClick: () => cgmManager.syncMissingPoints(),
      // },
      // {
      //   icon: <HardDrive className="w-5 h-5" />,
      //   label: "Hard sync",
      //   onClick: () => setShowHardSyncSheet(true),
      // },
      {
        icon: <CloudUpload className="w-5 h-5" />,
        label: "Upload logs",
        onClick: () => setShowUploadLogsSheet(true),
      },
      {
        icon: <RotateCcw className="w-5 h-5" />,
        label: "Reset & reconnect",
        // label: "Clear and restart",
        onClick: () => setShowClearRestartSheet(true),
      },
    ],
    []
  );

  const inactiveCgmOverlayOptions = useMemo(
    () => [
      {
        icon: <Plus className="w-5 h-5" />,
        label: "Connect",
        onClick: () => navigate(`/app/${supportProgramKey}/cgm-connection`),
      },
    ],
    []
  );

  // Full loader only on initial load (no data yet). Background refresh keeps content visible.
  if (dailyLogs.loading && !dailyLogs.data) {
    return (
      <div className="flex items-center justify-center h-52">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 space-y-4 pb-4">
      <CgmReadingCard />

      {/* Chart card - always visible for tab switching */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 mt-4 pb-4">
        <div className="px-4 pt-4">
          <CgmPeriodNavigator
            tabs={tabs}
            activeTab={activeTab}
            periodOffset={0}
            onTabChange={setActiveTab}
            onPrev={() => {}}
            onNext={() => {}}
            onDateSelect={setSelectedDate}
          />
        </div>
        {activeTab === "daily" && (
          <DayGlucoseTrendChart
            data={chartData}
            isLive={isToday}
            isLoading={dailyLogs.loading}
            // greenBand={{ min: CGM_IN_RANGE.MIN, max: CGM_IN_RANGE.MAX }}
          />
        )}
        {activeTab === "cumulative" && <PieChartRepresentation />}
      </div>

      {activeTab === "daily" && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 mt-4 p-4">
            <h3 className="text-base font-semibold mb-2">Metrics</h3>
            <div className="flex items-start gap-3 overflow-x-auto">
              {metrics.map((metric) => (
                <CgmMetricCard
                  key={metric.name}
                  metric={metric}
                  onMetricInfoClick={(metric) =>
                    setActiveDetailedLogsSheet(metric)
                  }
                />
              ))}
            </div>
          </div>
        </>
      )}

      <AddButtonOverlay
        options={
          !isCgmExpired && !isCgmDisconnected
            ? activeCgmOverlayOptions
            : inactiveCgmOverlayOptions
        }
      />

      <CGMHardSyncBottomSheet
        show={showHardSyncSheet}
        onClose={() => setShowHardSyncSheet(false)}
        onSubmit={() => {
          cgmManager.hardSync();
          setShowHardSyncSheet(false);
        }}
      />

      <CGMUploadLogsBottomSheet
        show={showUploadLogsSheet}
        onClose={() => setShowUploadLogsSheet(false)}
        onSubmit={() => {
          toast.info("Upload started...");
          cgmManager.generateUploadLogs();
          setShowUploadLogsSheet(false);
        }}
      />

      <CGMClearRestartBottomSheet
        show={showClearRestartSheet}
        onCancel={() => setShowClearRestartSheet(false)}
        onConfirm={() => {
          setShowClearRestartSheet(false);
          cgmManager.clearAndRestartConnection();
          dispatch(cgmActions.getCgmStatusRequest());
        }}
      />

      <CGMDetailedLogsBottomSheet
        show={!!activeDetailedLogsSheet}
        onClose={() => setActiveDetailedLogsSheet(null)}
        metric={activeDetailedLogsSheet}
      />
    </div>
  );
};

const InsightComponentGlucoseWrapper: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  const { statusAndSensorLife } = useAppSelector((state) => state.cgm);
  const dispatch = useAppDispatch();
  const [activeCgm, setActiveCgm] = useState<CgmStatusData | null>(null);

  useEffect(() => {
    if (statusAndSensorLife?.data) {
      setActiveCgm(statusAndSensorLife?.data?.[0] ?? null);
    }
  }, [statusAndSensorLife]);

  // useEffect(() => {
  //   if (activeCgm) {
  //     const isExpired =
  //       activeCgm.status === 'EXPIRED' ||
  //       activeCgm.endDate < new Date().toISOString();
  //     if (!isExpired) {
  //       getCgmStatus();
  //     }
  //   }
  // }, [activeCgm]);

  useEffect(() => {
    getCgmStatus();
  }, []);

  const getCgmStatus = () => {
    new Promise(() => {
      dispatch(cgmActions.getCgmStatusRequest());
    });
  };

  if (activeCgm) return <InsightComponentGlucose />;

  return (
    <div className="relative pb-12">
      <div className="px-4 space-y-4">
        <p className="text-sm text-gray-500">
          No CGM Device connected yet. Connect a CGM Device to see your glucose
          logs and more insights.
        </p>
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white p-8 flex flex-col items-center justify-center min-h-[220px]">
          <p className="text-sm font-medium text-gray-500 text-center">
            Connect a CGM Device to get started.
          </p>
        </div>
      </div>
      <AddButtonOverlay
        options={[
          {
            icon: <Plus className="w-5 h-5" />,
            label: "Connect",
            onClick: () => {
              navigate(`/app/${supportProgramKey}/cgm-connection`);
            },
          },
        ]}
      />
    </div>
  );
};

export default InsightComponentGlucoseWrapper;
